// Shared asset-audit boundary for text ingest and the standalone audit command.
// Results are content-addressed so an unchanged script reuses the sidecar while
// any edit makes the old result stale automatically.

import {
  buildAssetAuditSnapshot,
  type AssetAuditIssue,
} from "../../domain/ingest/asset-audit.js";
import type { ValidationIssue } from "../../domain/ingest/validation.js";
import { semanticScriptHash } from "../../infra/script-working-copy.js";
import { WorkspaceStore } from "../../infra/ingest/workspace-store.js";
import { runAssetAudit } from "../../llm/tasks/ingest/asset-audit.js";
import type { LlmTaskExecutor } from "../../llm/types.js";

export const ASSET_AUDIT_VERSION = "1";

export interface AssetAuditReport {
  audit_version: string;
  script_hash: string;
  created_at: string;
  summary: {
    issues: number;
    high: number;
    medium: number;
    low: number;
  };
  issues: AssetAuditIssue[];
}

const isReport = (value: unknown, scriptHash: string): value is AssetAuditReport => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const report = value as Partial<AssetAuditReport>;
  return report.audit_version === ASSET_AUDIT_VERSION
    && report.script_hash === scriptHash
    && report.summary !== null
    && typeof report.summary === "object"
    && Array.isArray(report.issues);
};

function reportFor(scriptHash: string, issues: AssetAuditIssue[]): AssetAuditReport {
  return {
    audit_version: ASSET_AUDIT_VERSION,
    script_hash: scriptHash,
    created_at: new Date().toISOString(),
    summary: {
      issues: issues.length,
      high: issues.filter((issue) => issue.confidence === "high").length,
      medium: issues.filter((issue) => issue.confidence === "medium").length,
      low: issues.filter((issue) => issue.confidence === "low").length,
    },
    issues,
  };
}

export async function auditScriptAssets(script: Record<string, unknown>, executor: LlmTaskExecutor, signal?: AbortSignal): Promise<AssetAuditReport> {
  const scriptHash = semanticScriptHash(script);
  const snapshot = buildAssetAuditSnapshot(script);
  const issues = await runAssetAudit(executor, snapshot, signal);
  return reportFor(scriptHash, issues);
}

export async function auditScriptAssetsCached(opts: {
  script: Record<string, unknown>;
  executor: LlmTaskExecutor;
  read: () => unknown;
  write: (report: AssetAuditReport) => void;
  exists: () => boolean;
  force?: boolean;
  signal?: AbortSignal;
}): Promise<{ report: AssetAuditReport; reused: boolean }> {
  const scriptHash = semanticScriptHash(opts.script);
  if (!opts.force && opts.exists()) {
    try {
      const cached = opts.read();
      if (isReport(cached, scriptHash)) {
        opts.executor.recordReuse("ingest.asset-audit");
        return { report: cached, reused: true };
      }
    } catch {
      // A malformed/stale sidecar is disposable; regenerate it from script.json.
    }
  }
  const report = await auditScriptAssets(opts.script, opts.executor, opts.signal);
  opts.write(report);
  return { report, reused: false };
}

export async function auditWorkspaceScriptAssets(
  store: WorkspaceStore,
  script: Record<string, unknown>,
  executor: LlmTaskExecutor,
  opts: { force?: boolean; signal?: AbortSignal } = {},
): Promise<{ report: AssetAuditReport; reused: boolean }> {
  return auditScriptAssetsCached({
    script,
    executor,
    read: () => store.readJson("asset-audit.json"),
    write: (report) => store.writeJson("asset-audit.json", report),
    exists: () => store.has("asset-audit.json"),
    ...(opts.force ? { force: true } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
}

const TYPE_LABEL: Record<AssetAuditIssue["type"], string> = {
  duplicate_actor: "duplicate actor",
  duplicate_location: "duplicate location",
  duplicate_prop: "duplicate prop",
  duplicate_state: "duplicate state",
  suspicious_singleton_state: "suspicious singleton state",
  unused_state: "unused state",
  needs_human_review: "asset needs human review",
};

export function assetAuditIssueLine(issue: AssetAuditIssue): string {
  const targets = [...issue.asset_ids, ...issue.state_ids].join(", ") || "<unknown>";
  const detail = issue.evidence || issue.recommendation || "review the referenced assets";
  return `${TYPE_LABEL[issue.type]} [${targets}]: ${detail}`;
}

export function assetAuditWarnings(report: AssetAuditReport): ValidationIssue[] {
  return report.issues.map((issue) => ({
    code: `asset_audit_${issue.type}`,
    path: "$",
    message: `${assetAuditIssueLine(issue)}; action=${issue.action}; confidence=${issue.confidence}`,
  }));
}
